import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl, BROADCASTERS_INLINE } from "../lib/api";
import Reveal from "../components/Reveal";
import { ArrowUpRight, Sparkles, Film, Megaphone } from "lucide-react";

const HERO_IMAGE =
    "https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?auto=format&fit=crop&w=2400&q=80";

const BROADCASTERS = BROADCASTERS_INLINE;

export default function Home() {
    const [talents, setTalents] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const t = await api.get("/talents");
                setTalents(t.data.slice(0, 8));
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    return (
        <div className="bg-black text-white" data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero">
                <img
                    src={HERO_IMAGE}
                    alt="N&M Management"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/25 to-black/90" />
                <div className="container-nm relative pb-16 md:pb-24 pt-36 w-full">
                    <Reveal>
                        <div className="kicker mb-5">N&M Management · Entertainment Studio</div>
                    </Reveal>
                    <Reveal delay={120}>
                        <h1 className="font-display text-[2.25rem] xs:text-5xl sm:text-6xl md:text-7xl lg:text-[6.5rem] xl:text-[8rem] font-extrabold leading-[0.95] tracking-[-0.035em] text-balance">
                            Creiamo format,<br />
                            <span className="text-[color:var(--brand-accent)]">produciamo</span> intrattenimento
                        </h1>
                    </Reveal>
                    <Reveal delay={240}>
                        <p className="mt-7 text-base md:text-xl text-white/80 max-w-2xl leading-relaxed">
                            Format originali, eventi memorabili, contenuti di marca. Portiamo l'idea
                            dal concept alla messa in onda — per brand e broadcaster.
                        </p>
                    </Reveal>
                    <Reveal delay={360}>
                        <div className="mt-9 flex flex-wrap gap-3">
                            <Link to="/produzioni-tv" className="btn-primary" data-testid="hero-cta-formats">
                                Scopri le produzioni <ArrowUpRight size={16} />
                            </Link>
                            <Link to="/contatti" className="btn-ghost text-white" data-testid="hero-cta-contact">
                                Contattaci
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* BROADCASTERS STRIP */}
            <section className="py-14 md:py-20 overflow-hidden" data-testid="broadcasters">
                <div className="container-nm mb-8">
                    <div className="kicker">Collaboriamo con broadcaster e realtà leader</div>
                </div>
                <div className="relative">
                    <div className="marquee">
                        {[...BROADCASTERS, ...BROADCASTERS, ...BROADCASTERS, ...BROADCASTERS].map((b, i) => (
                            <div
                                key={i}
                                className="flex items-center gap-16 px-12"
                                data-testid={`broadcaster-${b.key}-${i}`}
                            >
                                <img
                                    src={b.src}
                                    alt={b.name}
                                    loading="eager"
                                    className="h-14 md:h-20 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="py-24 md:py-36" data-testid="services">
                <div className="container-nm">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-16">
                        <Reveal className="md:col-span-5">
                            <div className="kicker mb-4">Cosa facciamo</div>
                            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-[-0.03em]">
                                Produzione, idee, regia.
                            </h2>
                        </Reveal>
                        <Reveal delay={120} className="md:col-span-6 md:col-start-7 flex items-end">
                            <p className="text-lg text-white/70 leading-relaxed">
                                Un team full-stack che unisce sensibilità editoriale e capacità produttiva, dallo
                                sviluppo format alla messa in onda, dalla regia di grandi eventi alla
                                comunicazione integrata per brand e broadcaster.
                            </p>
                        </Reveal>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <Film size={22} />,
                                title: "Produzioni TV",
                                text: "Sviluppo format, produzione esecutiva, casting, distribuzione per broadcast e digitale.",
                                to: "/produzioni-tv",
                                testId: "service-productions",
                            },
                            {
                                icon: <Sparkles size={22} />,
                                title: "Eventi",
                                text: "Eventi corporate, branded e legati a produzioni TV — concept, produzione, gestione.",
                                to: "/eventi",
                                testId: "service-events",
                            },
                            {
                                icon: <Megaphone size={22} />,
                                title: "Comunicazione",
                                text: "Strategie integrate: concept creativo, posizionamento, contenuti, attivazioni.",
                                to: "/comunicazione",
                                testId: "service-communication",
                            },
                        ].map((s, i) => (
                            <Reveal delay={i * 120} key={s.title}>
                                <Link
                                    to={s.to}
                                    data-testid={s.testId}
                                    className="group block border border-white/15 p-8 md:p-10 h-full transition-all duration-500 hover:bg-white hover:text-black hover:border-white"
                                >
                                    <div className="flex items-start justify-between mb-16">
                                        <div className="w-12 h-12 border border-current flex items-center justify-center">
                                            {s.icon}
                                        </div>
                                        <ArrowUpRight className="opacity-50 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500" />
                                    </div>
                                    <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4">
                                        {s.title}
                                    </h3>
                                    <p className="text-sm md:text-base opacity-70 leading-relaxed">
                                        {s.text}
                                    </p>
                                    <div className="mt-8 kicker">Scopri →</div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* TALENTS PREVIEW */}
            <section className="py-24 md:py-36" data-testid="home-talents">
                <div className="container-nm">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
                        <Reveal className="md:col-span-6">
                            <div className="kicker mb-4">Talent & Ospiti</div>
                            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-[-0.03em]">
                                Volti che raccontano il brand
                            </h2>
                        </Reveal>
                        <Reveal delay={120} className="md:col-span-5 md:col-start-8 flex items-end">
                            <p className="text-lg text-white/70">
                                Volti noti e protagonisti del mondo dello spettacolo hanno preso parte ai
                                nostri progetti televisivi ed eventi.
                            </p>
                        </Reveal>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                        {talents.map((t, i) => (
                            <Reveal delay={i * 80} key={t.id}>
                                <Link
                                    to={`/talent-ospiti/${t.slug}`}
                                    className="editorial-card group block"
                                    data-testid={`home-talent-${t.slug}`}
                                >
                                    <div className="img-wrap aspect-[3/4] bg-neutral-900">
                                        {t.cover_image && (
                                            <img
                                                src={mediaUrl(t.cover_image)}
                                                alt={t.title}
                                                loading="lazy"
                                                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                                            />
                                        )}
                                    </div>
                                    <div className="mt-4">
                                        <div className="font-display text-lg md:text-xl font-bold">
                                            {t.title}
                                        </div>
                                        <div className="kicker text-white/40 mt-1">{t.category}</div>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>

                    <div className="mt-12">
                        <Link to="/talent-ospiti" className="btn-ghost text-white" data-testid="home-talents-cta">
                            Scopri tutti →
                        </Link>
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="relative py-28 md:py-40 bg-[color:var(--brand-blue)] text-white overflow-hidden" data-testid="home-final-cta">
                <div className="container-nm relative z-10">
                    <Reveal>
                        <div className="kicker text-white/70 mb-6">Iniziamo insieme</div>
                    </Reveal>
                    <Reveal delay={100}>
                        <h2 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-[-0.035em] max-w-5xl">
                            Hai un progetto o vuoi collaborare con noi?
                        </h2>
                    </Reveal>
                    <Reveal delay={240}>
                        <div className="mt-12 flex flex-wrap gap-4">
                            <Link
                                to="/contatti?kind=format"
                                className="btn-ghost text-white"
                                data-testid="cta-propose-format"
                            >
                                Proponi un format
                            </Link>
                            <Link
                                to="/contatti?kind=event"
                                className="btn-ghost text-white"
                                data-testid="cta-request-event"
                            >
                                Richiedi un evento
                            </Link>
                            <Link
                                to="/contatti?kind=partner"
                                className="btn-ghost text-white"
                                data-testid="cta-become-partner"
                            >
                                Diventa partner
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
